import React, { useState, useEffect } from 'react';
import { Modal, Typography, List, Tag, Space, Button, Select, message } from 'antd';
import { TeamOutlined, CopyOutlined, GlobalOutlined, LockOutlined, CodeOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Dictionary } from '@onaio/utils/dist/types/types';
import { AkukoAPIService } from '../../../services/serviceClass';
import { IDENTITY_API, POSTS_API } from '../../../configs/env';
import { actionPostPublicEdit } from '../actions';
import { EmbedSettings } from '../Embed/EmbedSettings/index.js';
import { PostFolderService } from '../../../services/PostFolderService';
import { FolderUser, FolderGroup } from '../../../types/postFolders';
import './style.css';

const { Text } = Typography;
const { Option } = Select;

interface ShareModalProps {
  open: boolean;
  post: Dictionary;
  user: PermissionDisplay;
  onCancel: () => void;
}

interface PermissionDisplay {
  id: string;
  email?: string;
  name?: string;
  type: 'user' | 'group';
  role: 'admin' | 'edit' | 'view';
  image?: string;
  space_admin?: boolean;
}

const ShareModal: React.FC<ShareModalProps> = ({
  open,
  post,
  user,
  onCancel,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [embedModalVisible, setEmbedModalVisible] = useState(false);
  const [permissions, setPermissions] = useState<PermissionDisplay[]>([]);
  const [spaceUsers, setSpaceUsers] = useState<PermissionDisplay[]>([]);
  const dispatch = useDispatch();

  const loadPermissions = async () => {
    if (!post?.folder_id) return;
    
    try {
      setLoading(true);
      const service = new PostFolderService(`space/${post.space_id}/posts/folder`);
      const folder = await service.getFolderDetails(post.folder_id);
      
      const permissions: PermissionDisplay[] = [
        ...folder.metadata.users.map((user: FolderUser): PermissionDisplay => ({
          id: user.id,
          email: user.email,
          name: user?.name,
          image: user?.image,
          type: 'user',
          role: user.role,
          space_admin: user?.space_admin,
        })),
        ...folder.metadata.groups.map((group: FolderGroup): PermissionDisplay => ({
          id: group.id,
          name: group.name,
          type: 'group',
          role: group.role,
        })),
      ];
      setPermissions(permissions);
    } catch (error: any) {
    } finally {
      setLoading(false);
    }
  };

  const loadSpaceUsers = async (spaceId: string, accountId: string) => {
    try {
      setLoading(true);
      const service = new AkukoAPIService(IDENTITY_API, `user/space/${accountId}/${spaceId}`);
      const users = await service.list() as any[];
      const formattedUsers: PermissionDisplay[] = users.map((user: any) => ({
        id: user.uuid,
        email: user.email,
        name: user.name,
        type: 'user',
        role: user.spaces.find((space: any) => space.id === spaceId)?.role,
        image: user.image,
      }));
      setSpaceUsers(formattedUsers);
    } catch (error: any) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && post?.folder_id && !post?.public) {
      loadPermissions();
    }
  }, [open, post?.folder_id, post?.space_id, post?.public]);

  useEffect(() => {
    if (post && !post.folder_id && post.space_id && !post.public) {
      loadSpaceUsers(post.space_id, post.account_id);
    }
  }, [post, post?.public]);

  const handleAccessChange = (value: string) => {
    const status = value === 'public';
    const updatePost = { ...post, public: status };
    const service = new AkukoAPIService(POSTS_API, `post/${post.uuid}`);
    
    setLoading(true);
    service.update(updatePost)
      .then((res) => {
        const updatedPost = res as Dictionary;
        updatedPost.public
          ? message.success('Post is now public')
          : message.success('Post is now private');
        dispatch(
          actionPostPublicEdit({
            value: updatedPost.public,
            updated: updatedPost.updated,
          })
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const copyLinkToClipboard = () => {
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl)
      .then(() => {
        message.success('Link copied to clipboard');
      })
      .catch(() => {
        message.error('Failed to copy link');
      });
  };

  const handleEmbedPost = () => {
    if (!post.public) {
      message.warning('Post must be made public first');
      return;
    }
    setEmbedModalVisible(true);
  };

  const renderAvatar = (image?: string, name?: string, email?: string) => {
    if (image && image.trim() !== '') {
      return (
        <img 
          src={`https://assets.akuko.io/${image}`}
          alt={name || email}
          style={{ width: 32, height: 32, borderRadius: '50%' }}
        />
      );
    }
    
    // Get first letter from name or email
    const displayName = name || email || '';
    const firstLetter = displayName.charAt(0).toUpperCase();
    
    return (
      <div style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        backgroundColor: '#5C6BC0', // Indigo color
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '14px',
        fontWeight: 500
      }}>
        {firstLetter}
      </div>
    );
  };

  const renderPeopleWithAccess = () => {
    let dataSource = post?.folder_id ? permissions : spaceUsers;
    if (post?.space_id === null) {
        dataSource = [user];
    }
    return (
      <div>
        <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 16 }}>People with access</Text>
        <List
          dataSource={dataSource}
          loading={loading}
          renderItem={permission => (
            <List.Item
              key={permission.id}
              actions={[
                <Space>
                  <Tag color={
                    permission.role === 'admin' ? 'blue' : 
                    permission.role === 'edit' ? 'green' : 
                    'default'
                  }>
                    {permission.role === 'admin' ? 'Admin' :
                     permission.role === 'edit' ? 'Editor' :
                     'Viewer'}
                  </Tag>
                </Space>
              ]}
            >
              <List.Item.Meta
                avatar={
                  permission.type === 'user' 
                    ? renderAvatar(permission.image, permission.name, permission.email)
                    : (
                      <div style={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: '#1890ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <TeamOutlined style={{ color: 'white', fontSize: '18px' }} />
                      </div>
                    )
                }
                title={
                  <>
                    {permission.name || permission.email?.split('@')[0]}
                    {permission.space_admin && <Tag color="blue" style={{ marginLeft: 8 }}>Space Admin</Tag>}
                  </>
                }
                description={permission.type === 'group' ? 'Group' : permission.email}
              />
            </List.Item>
          )}
        />
      </div>
    );
  };

  return (
    <>
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <TeamOutlined style={{ marginRight: 8 }} />
            <span>
              Share {post?.title && <span className="folder-name">"{post.title}"</span>}
            </span>
          </div>
        }
        open={open}
        onCancel={onCancel}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              icon={<CopyOutlined />} 
              onClick={copyLinkToClipboard}
            >
              Copy link
            </Button>
            <Button type="primary" onClick={onCancel}>
              Done
            </Button>
          </div>
        }
        width={560}
        className="folder-permissions-modal view-only"
        centered
        maskClosable
      >
        <div>
          {renderPeopleWithAccess()}
          
          <div style={{ marginTop: 24 }}>
            <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 16 }}>General access</Text>
            
            <div className="access-container">
              <div className="general-access-row">
                <div className="access-info">
                  <div className="access-info-icon">
                    {post?.public ? 
                      <GlobalOutlined style={{ fontSize: 18 }} /> : 
                      <LockOutlined style={{ fontSize: 18 }} />
                    }
                  </div>
                  <div className="access-info-select">
                    <Select 
                      defaultValue={post?.public ? 'public' : 'restricted'} 
                      style={{ width: 200 }}
                      onChange={handleAccessChange}
                      disabled={loading}
                    >
                      <Option value="public">Anyone with the link</Option>
                      <Option value="restricted">Restricted</Option>
                    </Select>
                    <div className="access-description">
                      {post?.public 
                        ? 'Anyone on the internet with the link can view' 
                        : 'Only people with access can open'}
                    </div>
                  </div>
                </div>
                
                {post?.public && (
                  <Button 
                    type="link" 
                    onClick={handleEmbedPost}
                    icon={<CodeOutlined />}
                  >
                    Embed post
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <EmbedSettings
        showModal={embedModalVisible}
        onCancel={() => setEmbedModalVisible(false)}
        componentId={post?.uuid}
      />
    </>
  );
};

export default ShareModal; 